/**
 * Authored line icons — one consistent 1.6px stroke, 24px grid, currentColor.
 * No icon-font, no emoji; each is a drawn SVG so the rail and controls carry a
 * single visual voice. `aria-hidden` by default — icons sit beside real labels.
 */
import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

function Base({ children, ...props }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...props}
    >
      {children}
    </svg>
  );
}

export const IconClients = (p: IconProps) => (
  <Base {...p}>
    <circle cx="9" cy="8" r="3.2" />
    <path d="M3.5 19a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.2a3 3 0 0 1 0 5.6" />
    <path d="M17.5 13.5A5.5 5.5 0 0 1 20.5 18.5" />
  </Base>
);

export const IconBrands = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 10 12 4l8 6" />
    <path d="M5 9.5V19a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V9.5" />
    <path d="M10 20v-5h4v5" />
  </Base>
);

export const IconDashboard = (p: IconProps) => (
  <Base {...p}>
    <rect x="3.5" y="3.5" width="7" height="8" rx="1.2" />
    <rect x="13.5" y="3.5" width="7" height="5" rx="1.2" />
    <rect x="3.5" y="14.5" width="7" height="6" rx="1.2" />
    <rect x="13.5" y="11.5" width="7" height="9" rx="1.2" />
  </Base>
);

export const IconOnboarding = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 7h11" />
    <path d="M4 12h7" />
    <path d="M4 17h5" />
    <path d="m15 15.5 2.2 2.2L21 13.5" />
  </Base>
);

export const IconData = (p: IconProps) => (
  <Base {...p}>
    <ellipse cx="12" cy="5.5" rx="7" ry="2.6" />
    <path d="M5 5.5v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-6" />
    <path d="M5 11.5v6c0 1.4 3.1 2.6 7 2.6s7-1.2 7-2.6v-6" />
  </Base>
);

export const IconBack = (p: IconProps) => (
  <Base {...p}>
    <path d="M15 5l-7 7 7 7" />
  </Base>
);

export const IconOrders = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 4h9l4 4v12a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1Z" />
    <path d="M14 4v4h4" />
    <path d="M8 13h7M8 16.5h5" />
  </Base>
);

export const IconStatements = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 19V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v14l-2.5-1.6L15 19l-3-1.6L9 19l-2.5-1.6Z" />
    <path d="M8 8.5h8M8 12h5" />
  </Base>
);

export const IconPayouts = (p: IconProps) => (
  <Base {...p}>
    <rect x="3.5" y="6" width="17" height="12" rx="1.6" />
    <circle cx="12" cy="12" r="2.6" />
    <path d="M7 9v6M17 9v6" />
  </Base>
);

export const IconCatalog = (p: IconProps) => (
  <Base {...p}>
    <rect x="4" y="4" width="7" height="7" rx="1.2" />
    <rect x="13" y="4" width="7" height="7" rx="1.2" />
    <rect x="4" y="13" width="7" height="7" rx="1.2" />
    <rect x="13" y="13" width="7" height="7" rx="1.2" />
  </Base>
);

export const IconCampaigns = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 9v6l12 4V5L4 9Z" />
    <path d="M16 7v10M19 9.5v5" />
  </Base>
);

export const IconInsights = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 20V4" />
    <path d="M4 20h16" />
    <path d="M7.5 16l3.5-4 3 2.5L20 7" />
  </Base>
);

export const IconBell = (p: IconProps) => (
  <Base {...p}>
    <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.7 21a2 2 0 0 1-3.4 0" />
  </Base>
);

export const IconSwitch = (p: IconProps) => (
  <Base {...p}>
    <path d="M4 8h12l-3-3" />
    <path d="M20 16H8l3 3" />
  </Base>
);

export const IconEmpty = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M8.5 12h7" />
  </Base>
);

export const IconIngestion = (p: IconProps) => (
  <Base {...p}>
    <path d="M12 3v9" />
    <path d="m8.5 8.5 3.5 3.5 3.5-3.5" />
    <path d="M4 14v4a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-4" />
  </Base>
);

export const IconBilling = (p: IconProps) => (
  <Base {...p}>
    <path d="M6 3.5h12a1 1 0 0 1 1 1V20.5l-2.5-1.4-2.5 1.4-2.5-1.4-2.5 1.4L6 20.5V4.5a1 1 0 0 1 1-1Z" />
    <path d="M9 8.5h6M9 12h6M9 15.5h3.5" />
  </Base>
);

export const IconConnections = (p: IconProps) => (
  <Base {...p}>
    <path d="M5 12.55a11 11 0 0 1 14 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <path d="M12 20h.01" />
  </Base>
);

export const IconSettings = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M12 2.5v2.2M12 19.3v2.2M4.2 7l1.9 1.1M17.9 15.9l1.9 1.1M4.2 17l1.9-1.1M17.9 8.1 19.8 7" />
  </Base>
);

/**
 * The AI "spark" mark — Clarity's assistant signature. A four-point sparkle with
 * a small companion star; drawn (never emoji) so it renders everywhere and shares
 * the one icon voice. Used only on AI moments (banner, recommendations, advisor).
 * Filled via currentColor by default; on the spark tile it sits on the gradient.
 */
export const IconSpark = (p: IconProps) => (
  <Base strokeWidth="2" {...p}>
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3Z" />
    <path d="M19 15l.7 1.9 1.8.6-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.6L19 15Z" />
  </Base>
);

/** Sun mark for the Light theme toggle. */
export const IconSun = (p: IconProps) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </Base>
);

/** Moon mark for the Dark theme toggle. */
export const IconMoon = (p: IconProps) => (
  <Base {...p}>
    <path d="M20 14.5A8 8 0 0 1 9.5 4 7 7 0 1 0 20 14.5Z" />
  </Base>
);
